const mode = "live";
// Per-tenant runtime config: deploy-frontend.yml writes /env.js next to index.html
// in the tenant's S3 prefix, which sets window.__ENV__ before the bundle loads.
// When absent (local dev, legacy per-branch builds) the build-time values below apply.
const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {};
const site = runtimeEnv.TENANT || process.env.REACT_APP_TENANT || "ifacet";
// Tenant-agnostic builds (build-frontends.yml sets REACT_APP_RUNTIME_TENANT) must
// get the tenant from env.js — failing loudly beats serving another tenant's app.
if (process.env.REACT_APP_RUNTIME_TENANT && !runtimeEnv.TENANT) {
    throw new Error("env.js missing or invalid: no tenant configured for this deployment");
}
const siteUrl = runtimeEnv.TUNINGFORK_URL || process.env.REACT_APP_TUNINGFORK_URL || "https://" + site + ".edusquares.com/";
// const siteUrl = "http://localhost:8080/tuningfork/";

// const siteUrl = "https://erp.nttftrg.com/" // for nttf

const nodeSiteUrl = runtimeEnv.NODE_API || process.env.REACT_APP_NODE_API || `https://${site}-node.edusquares.com/api/`;
// const nodeSiteUrl = `http://localhost:8080/api/`

// Authentication backend selection ('java' or 'node')
const authBackend = process.env.REACT_APP_AUTH_BACKEND || 'java';
// For node backend, use nodeSiteUrl with v1 prefix; for java, use siteUrl
const authBaseUrl = authBackend === 'node' ? nodeSiteUrl + 'v1/' : siteUrl;

// Pre-login init endpoints (theme + institute/home). These follow the same
// java/node switch as auth: on node they resolve to
// `${nodeSiteUrl}v1/rest/mobile/ilearn/`, matching the ports in
// nodefork/src/routes/v1/ilearn-init.routes.ts. On java they equal apiUrl.
const initApiUrl = (authBackend === 'node' ? authBaseUrl : siteUrl) + 'rest/mobile/ilearn/';

// Tracking API URL — derives from the auth backend when not explicitly configured,
// so tenant-agnostic builds need no build-time REACT_APP_TRACKING_URL override.
const trackingBaseUrl = process.env.REACT_APP_TRACKING_URL ||
    (authBackend === 'node'
        ? nodeSiteUrl + 'v1/study-resources/learning/track'
        : siteUrl + 'api/studyresources/learning/track/');

// --- Lambda configuration ---
// Switch between the live AWS Lambda Function URLs ("server") and a local dev
// server ("local"). The committed default is ALWAYS "server" - the only thing
// that flips it is an untracked .env.local (see .env.local.example).
const lambdaTarget = process.env.REACT_APP_LAMBDA_TARGET || "server"; // "server" | "local"
const lambdaLocalBase = process.env.REACT_APP_LAMBDA_LOCAL_BASE || "http://localhost:4000";

// Production Function-URL hosts (committed defaults - never change on commit)
const lambdaServer = {
    testAnalysis:         "https://qtzeg3y5oc5iedabtawyzodfz40evpsi.lambda-url.ap-southeast-1.on.aws",
    msg91Otp:             "https://4lkooqi5fbwptop7hgsas5hdnu0wyrig.lambda-url.ap-southeast-1.on.aws",
    testResume:           "https://5or2in4w2avvenkbbm2fpetjle0fijue.lambda-url.ap-southeast-1.on.aws",
    testResponses:        "https://soaz3ulallctn6szdu3wwmgmaa0lkepj.lambda-url.ap-southeast-1.on.aws",
    proctorProfile:       "https://yazgub22cf6v3ry2cms3gvkhra0xmevx.lambda-url.ap-southeast-1.on.aws",
    testSolutions:        "https://fazfu5ovtyloaojysslqrnfobq0kitms.lambda-url.ap-southeast-1.on.aws",
    coins:                "https://sndkzoujjielrmtxj2eypo2hii0zjzvv.lambda-url.ap-southeast-1.on.aws",
    clock:                "https://rmyo34tm4l7e77llimeksvi47u0cqntp.lambda-url.ap-southeast-1.on.aws",
    verifyMicrosoftToken: "https://meman2zf2a2lbyafipwhvqrjru0wvrqi.lambda-url.ap-southeast-1.on.aws",
    vdocipher:            "https://w2kzlmanhlmlmmzanasicnxkou0nnwtn.lambda-url.ap-southeast-1.on.aws",
    s3SignedKey:          "https://wu6wfeyishrmjyrnbydzxfbl2i0ldoef.lambda-url.ap-southeast-1.on.aws",
};

// Local paths under the single dev server (only functions with a local handler).
// Keys absent here fall back to the server host even in "local" mode.
const lambdaLocalPath = {
    testAnalysis:         "/testAnalysis",
    msg91Otp:             "/Msg91OTPAuth",
    testResume:           "/testResume",
    testResponses:        "/testResponses",
    proctorProfile:       "/proctorDynamodb",
    testSolutions:        "/testSolutions",
    coins:                "/coins",
    verifyMicrosoftToken: "/verify-microsoft-token",
    vdocipher:            "/authenticateVdocipher",
    // coins, clock, s3SignedKey: no local handler -> stay on server.
};

const lambda = {};
Object.keys(lambdaServer).forEach((k) => {
    lambda[k] =
        lambdaTarget === "local" && lambdaLocalPath[k]
            ? lambdaLocalBase + lambdaLocalPath[k] // local handler exists
            : lambdaServer[k];                     // server, or no local impl
});

// Per-tenant online-test backend for the ported Function-URL calls
// (responses / resume / clock). "node" routes them to nodefork's
// /v1/online-tests/fn/* endpoints (DynamoDB-backed, same contract) instead of
// the AWS Lambda URLs. Set via env.js (deploy-frontend writes ONLINE_TEST_FNS
// per tenant); defaults to "lambda" so untouched tenants are unaffected.
const onlineTestFns = runtimeEnv.ONLINE_TEST_FNS || process.env.REACT_APP_ONLINE_TEST_FNS || "lambda";

// Base configuration
const BaseConfig = {
    siteTitle: site,
    siteMode:  mode,
    siteUrl :  siteUrl,
    nodeSiteUrl,
    authBackend,
    authBaseUrl,
    initApiUrl,
    trackingBaseUrl,
    lambdaTarget,
    lambda,
    onlineTestFns,
    baseUrl :  siteUrl + 'rest/mobile/',
    apiUrl  :  siteUrl + 'rest/mobile/ilearn/',
    getBatch: siteUrl + 'api/common/batches?',
    getBatchforEnquiry: siteUrl + 'api/common/enquiry/',
    getLearnersforBatchId :  siteUrl + 'api/common/batches/',
    logo    :  "https://webfront.edusquares.com/" + site + "/files/pwa/logo.jpg",
    cover   :  "https://webfront.edusquares.com/" + site + "/files/pwa/cover.jpg",
    pdfUrl  :  siteUrl,
    desktopApi  :  siteUrl + 'api/',
    AWSRegion: "ap-southeast-1",
    AWSEndpoint: '',
    AWSAccessKeyId: "",
    AWSSecretAccessKey: "",
    CONNECTION_ERROR_MSG: "The server is taking too long to respond. Please try again later.",
    INTERNET_ERROR_MSG: "You appear to be offline. Please check your internet connection and try again.",

    // GIGW Compliance Configuration
    gigwMode: false, // Set to true to enable GIGW compliance features

    // Organization Configuration (used when gigwMode is enabled)
    organization: {
        type: "private", // "government", "educational", "private"
        name: site + " Learning Platform",
        contactEmail: "support@" + site + ".com",
        privacyEmail: "privacy@" + site + ".com",
        supportPhone: "+91-40-4020-4020",
        address: "Learning Platform Address",
        dpoName: "Data Protection Officer",
        companyName: "Your Company Name",

        // Government specific fields (only used when type is "government")
        governmentEmblem: "/assets/images/govt-emblem.png",
        ministryName: "Ministry Name",
        departmentName: "Department Name",
        govtWebsiteUrl: "https://ministry.gov.in"
    },

    // Feature toggles for GIGW compliance
    features: {
        mandatoryPages: false, // Privacy Policy, Terms, Grievance, About Us
        lastUpdatedTimestamps: false, // Show last updated dates
        contentOwnership: false, // Show content owner information
        multiLanguage: false, // Hindi + regional language support
        governmentBranding: false, // Government logos and branding
        apiIntegration: false, // Government API integration readiness
        accessibilityEnhanced: true, // Always enabled - WCAG compliance
        grievancePortal: false, // Formal grievance redressal system
        archivePolicy: false // Content archival and version management
    }
};

// Merge with test override if available (for development/testing)
const Config = (() => {
    if (typeof window !== 'undefined' && window.GIGW_TEST_OVERRIDE) {
        return {
            ...BaseConfig,
            ...window.GIGW_TEST_OVERRIDE,
            organization: {
                ...BaseConfig.organization,
                ...window.GIGW_TEST_OVERRIDE.organization
            },
            features: {
                ...BaseConfig.features,
                ...window.GIGW_TEST_OVERRIDE.features
            }
        };
    }
    return BaseConfig;
})();

export const OpenLoginTypes = {
    //Open Login Types
    OPEN_LOGIN_TYPE_FACEBOOK : 1,
    OPEN_LOGIN_TYPE_GOOGLE : 2 
}

const CURRENT_PORTLET = null;

export default Config;